import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import {
  Star, ArrowLeft, CircleNotch, DownloadSimple, CaretUp, CaretDown,
  Target, ArrowUp, ArrowDown, Minus,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { syncConstraintsToDB } from '../components/MyPrediction';

import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
import FormulaExplainer from '../components/FormulaExplainer';
import ConvergencePanel from '../components/ConvergencePanel';
import BacktestPanel from '../components/BacktestPanel';
import BlindSpotsBanner, { buildTopPicks } from '../components/BlindSpotsBanner';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';
import W3FamilyFinalVerdict from '../components/W3FamilyFinalVerdict';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ClassBadge = ({ value }) => {
  if (value === 'UP') return <span className="inline-flex items-center gap-0.5 text-green-700 bg-green-100 rounded px-1 py-0.5 text-[10px] font-bold"><ArrowUp size={8} weight="bold" />UP</span>;
  if (value === 'DOWN') return <span className="inline-flex items-center gap-0.5 text-red-700 bg-red-100 rounded px-1 py-0.5 text-[10px] font-bold"><ArrowDown size={8} weight="bold" />DN</span>;
  return <span className="inline-flex items-center gap-0.5 text-gray-600 bg-gray-100 rounded px-1 py-0.5 text-[10px] font-bold"><Minus size={8} weight="bold" />0</span>;
};

const PatternDisplay = ({ pattern, compact }) => {
  const parts = pattern.split(',');
  // Show separator between B5 and LS1
  return (
    <div className="flex items-center gap-0.5 flex-wrap">
      {parts.slice(0, 5).map((p, i) => <ClassBadge key={i} value={p} />)}
      <span className="text-gray-400 font-bold mx-0.5">|</span>
      {parts.slice(5).map((p, i) => <ClassBadge key={`ls${i}`} value={p} />)}
    </div>
  );
};

const ResizableHeader = ({ children, width, onResize, columnKey, onClick, className = '' }) => {
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const handleMouseDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    startXRef.current = e.clientX; startWidthRef.current = width;
    const move = (ev) => { onResize(columnKey, Math.max(40, startWidthRef.current + (ev.clientX - startXRef.current))); };
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
  };
  return (
    <th className={`relative ${className}`} style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }} onClick={onClick}>
      <div className="flex items-center justify-center gap-1 pr-2 font-bold">{children}</div>
      <div className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-500/30 group flex items-center justify-center" onMouseDown={handleMouseDown} onClick={(e) => e.stopPropagation()}>
        <div className="w-0.5 h-4 bg-gray-300 group-hover:bg-blue-500 transition-colors" />
      </div>
    </th>
  );
};

const DrawRow = ({ d, idx }) => (
  <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
    <td className="px-1 py-1.5 border-b border-gray-100 font-mono text-center text-gray-500 text-xs">
      {new Date(2026, d.month - 1).toLocaleString('default', { month: 'short' })}
    </td>
    <td className="px-1 py-1.5 border-b border-gray-100 font-mono text-center text-xs">{d.year}</td>
    <td className="px-1 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/30">{d.b1}</td>
    <td className="px-1 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/30">{d.b2}</td>
    <td className="px-1 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/30">{d.b3}</td>
    <td className="px-1 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/30">{d.b4}</td>
    <td className="px-1 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/30">{d.b5}</td>
    <td className="px-1 py-1.5 border-b border-gray-100 font-mono text-center bg-amber-50/30">{d.ls1}</td>
    <td className="px-1 py-1.5 border-b border-gray-100 font-mono text-center bg-amber-50/30">{d.ls2}</td>
    <td className="px-1 py-1.5 border-b border-gray-100"><PatternDisplay pattern={d.pattern} /></td>
    <td className="px-1 py-1.5 border-b border-gray-100 font-mono text-center bg-green-50/50 font-bold text-xs">
      {(d.pattern_pct * 100).toFixed(1)}%
    </td>
  </tr>
);

const TotalRow = ({ label, data, count, extraCls = 'bg-gray-200' }) => (
  <tr className={`${extraCls} font-bold`}>
    <td className="px-1 py-1.5 border-b border-gray-300 text-center text-xs" colSpan={2}>{label}</td>
    <td className="px-1 py-1.5 border-b border-gray-300 font-mono text-center">{data.ball_1}</td>
    <td className="px-1 py-1.5 border-b border-gray-300 font-mono text-center">{data.ball_2}</td>
    <td className="px-1 py-1.5 border-b border-gray-300 font-mono text-center">{data.ball_3}</td>
    <td className="px-1 py-1.5 border-b border-gray-300 font-mono text-center">{data.ball_4}</td>
    <td className="px-1 py-1.5 border-b border-gray-300 font-mono text-center">{data.ball_5}</td>
    <td className="px-1 py-1.5 border-b border-gray-300 font-mono text-center">{data.lucky_star_1}</td>
    <td className="px-1 py-1.5 border-b border-gray-300 font-mono text-center">{data.lucky_star_2}</td>
    <td colSpan={2} className="px-1 py-1.5 border-b border-gray-300 text-center text-xs text-gray-600">{count} draws</td>
  </tr>
);

export default function Window5SevenCombo() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [patternSort, setPatternSort] = useState({ key: 'times', dir: 'desc' });
  const [showAll, setShowAll] = useState(false);
  const [histPage, setHistPage] = useState(1);
  const [mainTab, setMainTab] = useState('convergence');
  const [myComboPattern, setMyComboPattern] = useState('');
  const [comboApplied, setComboApplied] = useState(false);
  const [comboSet, setComboSet] = useState(1);
  const [dbSets, setDbSets] = useState({});

  const handleComboSetChange = (s) => {
    setComboSet(s);
    const saved = dbSets[`C${s}`]?.combo_pattern || '';
    setMyComboPattern(saved);
    setComboApplied(!!saved);
  };
  const HIST_PER_PAGE = 100;

  const [colWidths, setColWidths] = useState({ rank: 50, series: 260, times: 55, pct: 60, expGap: 55, gap: 50, overdue: 60, predScore: 65 });
  const handleResize = (key, w) => setColWidths(prev => ({ ...prev, [key]: w }));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analysis/seven-combo`);
      setData(res.data);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDrawsUpdated(fetchData);

  // Load constraint sets from DB
  useEffect(() => {
    (async () => {
      try {
        const csRes = await axios.get(`${API}/analysis/constraint-sets`);
        const cs = csRes.data || {};
        setDbSets(cs);
        const c1Combo = cs.C1?.combo_pattern || '';
        setMyComboPattern(c1Combo);
        setComboApplied(!!c1Combo);
      } catch {}
    })();
  }, []);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await axios.get(`${API}/analysis/seven-combo/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `seven_combo_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success('Export completed');
    } catch { toast.error('Failed to export'); }
    finally { setExportLoading(false); }
  };

  const handlePatternSort = (key) => {
    setPatternSort(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  };

  const sortedPatterns = data?.ranked_patterns ? [...data.ranked_patterns].sort((a, b) => {
    const av = a[patternSort.key] ?? 0, bv = b[patternSort.key] ?? 0;
    return patternSort.dir === 'desc' ? bv - av : av - bv;
  }) : [];
  const displayPatterns = showAll ? sortedPatterns : sortedPatterns.slice(0, 30);
  const predictedPatterns = new Set((data?.prediction || []).slice(0, 10).map(p => p.pattern));

  const cyGrouped = (data?.current_year_draws || []).reduce((acc, d) => {
    if (!acc[d.month]) acc[d.month] = [];
    acc[d.month].push(d);
    return acc;
  }, {});
  const cyMonthOrder = Object.keys(cyGrouped).map(Number).sort((a, b) => b - a);

  const histDraws = data?.historical_draws || [];
  const totalHistPages = Math.ceil(histDraws.length / HIST_PER_PAGE);
  const pagedHist = histDraws.slice(0, histPage * HIST_PER_PAGE);
  const histGrouped = pagedHist.reduce((acc, d) => {
    if (!acc[d.year]) acc[d.year] = [];
    acc[d.year].push(d);
    return acc;
  }, {});
  const histYearOrder = Object.keys(histGrouped).map(Number).sort((a, b) => b - a);

  const SortIcon = ({ k }) => {
    if (patternSort.key !== k) return null;
    return patternSort.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;
  };

  const headerCls = "px-1 py-2 text-xs uppercase tracking-wider text-gray-700 cursor-pointer hover:bg-gray-200 text-center";

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-700"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>Window 3.2 (A-X) (All 7 Balls)</h1>
                <p className="text-xs text-gray-500">7-Combo Pattern (B1-B5 + LS1 + LS2)</p>
              </div>
            </div>
            <Button onClick={handleExport} disabled={exportLoading} className="btn-secondary flex items-center gap-2" data-testid="export-btn">
              {exportLoading ? <CircleNotch size={18} className="animate-spin" /> : <DownloadSimple size={18} />}
              Export Excel
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-6">


        <W3FamilyFinalVerdict activeWindow="W3.2" />
        <WindowNarrative windowId="W3_2" {...NARRATIVES.W3_2} className="mb-5" />
        {loading ? (
          <div className="flex items-center justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Total Transitions</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.total_transitions}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Unique 7-Combos</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.ranked_patterns?.length || 0}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4 bg-blue-50 border-blue-200">
                <p className="text-xs uppercase tracking-wider font-bold text-blue-600">Current Year</p>
                <p className="text-2xl font-bold text-blue-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.current_year}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Historical Draws</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{histDraws.length}</p>
              </div>
            </div>

            {/* My 7-Combo Pattern Prediction */}
            <div className={`rounded-lg p-5 text-white transition-all mb-6 ${comboApplied ? 'bg-gradient-to-r from-emerald-600 to-green-700 ring-2 ring-emerald-400' : 'bg-gradient-to-r from-gray-600 to-gray-700'}`} data-testid="my-prediction-combo">
              {/* C1/C2/C3 selector */}
              <div className="flex items-center gap-1.5 mb-3">
                {[1,2,3,4,5].map(s => (
                  <button key={s} onClick={() => handleComboSetChange(s)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${comboSet === s ? (s===1?'bg-blue-600':s===2?'bg-purple-600':'bg-teal-600')+' text-white ring-2 ring-white/40 shadow-lg' : !!dbSets[`C${s}`]?.combo_pattern ? 'bg-white/20 text-white/90' : 'bg-white/10 text-white/50 hover:bg-white/15'}`} data-testid={`combo-set-${s}`}>C{s}</button>
                ))}
                <span className="text-[10px] text-white/40 ml-2">5 independent sets</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-bold text-lg">My 7-Combo Pattern Prediction</h3>
                  <p className="text-xs text-white/70">Set your predicted UP/DOWN pattern for B1-B5 + LS1 + LS2 — applies across prediction windows</p>
                </div>
                {comboApplied ? (
                  <span className="text-xs bg-emerald-400/30 px-3 py-1.5 rounded-full font-bold flex items-center gap-1.5 ring-1 ring-emerald-300">
                    <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span> APPLIED
                  </span>
                ) : (
                  <span className="text-xs bg-white/10 px-3 py-1.5 rounded-full font-bold text-white/60">NOT SET</span>
                )}
              </div>

              {/* C1-C5 overview strip */}
              <div className="flex flex-wrap gap-2 mb-3 bg-white/10 rounded-lg px-4 py-2.5">
                {[1,2,3,4,5].map(s => {
                  const val = dbSets[`C${s}`]?.combo_pattern;
                  return (
                    <div key={s} className={`flex items-center gap-1.5 text-xs ${comboSet === s ? 'ring-1 ring-white/40 rounded px-1.5 py-0.5 bg-white/10' : ''}`}>
                      <span className={`font-bold ${val ? 'text-emerald-300' : 'text-white/30'}`}>C{s}:</span>
                      {val ? (
                        <span className="font-mono font-bold text-white/90">
                          {val.split(',').map(v => v === 'UP' ? '▲' : v === 'DOWN' ? '▼' : '—').join('')}
                        </span>
                      ) : (
                        <span className="text-white/30 italic">not set</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {['B1','B2','B3','B4','B5','LS1','LS2'].map((b, i) => {
                  const dirs = myComboPattern ? myComboPattern.split(',') : [];
                  const val = dirs[i] || '';
                  return (
                    <div key={b} className="flex flex-col items-center gap-1">
                      <span className={`text-[10px] font-bold ${i >= 5 ? 'text-amber-300' : 'opacity-70'}`}>{b}</span>
                      <select value={val} onChange={e => {
                        const newDirs = myComboPattern ? myComboPattern.split(',') : ['','','','','','',''];
                        while (newDirs.length < 7) newDirs.push('');
                        newDirs[i] = e.target.value;
                        setMyComboPattern(newDirs.join(','));
                        setComboApplied(false);
                      }} className="w-20 px-1 py-1.5 rounded text-gray-900 font-mono font-bold text-center text-xs" data-testid={`combo-select-${i}`}>
                        <option value="">-</option>
                        <option value="UP">UP</option>
                        <option value="DOWN">DOWN</option>
                      </select>
                    </div>
                  );
                })}
                {data?.top4_predictions?.[0]?.pattern && (
                  <button onClick={() => { setMyComboPattern(data.top4_predictions[0].pattern); setComboApplied(false); }}
                    className="px-2 py-1 rounded text-[10px] font-mono font-bold bg-white/15 hover:bg-white/25 transition-colors ml-1">
                    Top: {data.top4_predictions[0].pattern}
                  </button>
                )}
                <Button onClick={() => {
                  setComboApplied(true); setDbSets(prev => ({ ...prev, [`C${comboSet}`]: { ...prev[`C${comboSet}`], combo_pattern: myComboPattern } })); syncConstraintsToDB(comboSet, { combo_pattern: myComboPattern }); toast.success(`7-Combo C${comboSet} applied: ${myComboPattern}`); }}
                  className={`font-bold transition-all ${comboApplied ? 'bg-emerald-500 text-white hover:bg-emerald-400 ring-2 ring-emerald-300' : 'bg-amber-400 text-amber-900 hover:bg-amber-300'}`} data-testid="save-combo-btn">
                  {comboApplied ? 'Applied' : 'Save Constraint'}
                </Button>
                {comboApplied && (
                  <Button onClick={() => { setComboApplied(false); setMyComboPattern(''); setDbSets(prev => ({ ...prev, [`C${comboSet}`]: { ...prev[`C${comboSet}`], combo_pattern: null } })); syncConstraintsToDB(comboSet, { combo_pattern: null }); toast.info(`7-Combo C${comboSet} cleared`); }}
                    variant="ghost" className="text-white/70 hover:text-white text-xs">Clear</Button>
                )}
              </div>
            </div>

            {/* Formula Explainer (collapsible) */}
            <FormulaExplainer context="seven-combo" />

            {/* Tab selector — Convergence, F1, F2 + original tabs preserved */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-6 flex-wrap">
              <button onClick={() => setMainTab('convergence')} className={`flex-1 min-w-[140px] px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'convergence' ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`} data-testid="tab-convergence">
                🎯 Convergence
              </button>
              <button onClick={() => setMainTab('formula1')} className={`flex-1 min-w-[140px] px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'formula1' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`} data-testid="tab-formula1">
                Formula 1 (Long-term)
              </button>
              <button onClick={() => setMainTab('formula2')} className={`flex-1 min-w-[140px] px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'formula2' ? 'bg-orange-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`} data-testid="tab-formula2">
                Formula 2 (Next-draw)
              </button>
              <button onClick={() => setMainTab('rankings')} className={`flex-1 min-w-[140px] px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'rankings' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-200'}`} data-testid="tab-rankings">
                Rankings & History
              </button>
              <button onClick={() => setMainTab('top4pred')} className={`flex-1 min-w-[140px] px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'top4pred' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-200'}`} data-testid="tab-top4pred">
                Top 4 Combo Predictions
              </button>
            </div>

            {/* === CONVERGENCE TAB === */}
            {mainTab === 'convergence' && data.convergence && (
              <div className="space-y-5">
                <BacktestPanel endpoint="seven-combo" />
                <BlindSpotsBanner
                  riskFlags={data.risk_flags}
                  topPicks={buildTopPicks(data.convergence)}
                  itemLabel="combos"
                  renderItem={(p) => <SevenComboBadge pattern={p} />}
                />
                <ConvergencePanel
                  convergence={data.convergence}
                  itemLabel="Pattern"
                  renderItem={(p) => <SevenComboBadge pattern={p} />}
                  f1Lookup={(p) => {
                    const m = (data.ranked_patterns || []).find(r => r.series === p);
                    return m ? { score: m.pred_score, score_v_adj: m.pred_score_volatility_adj } : null;
                  }}
                  f2Lookup={(p) => {
                    const m = (data.ranked_patterns || []).find(r => r.series === p);
                    return m ? { score: m.combined_score_v2 } : null;
                  }}
                />
              </div>
            )}

            {/* === FORMULA 1 TAB (Long-term) === */}
            {mainTab === 'formula1' && (
              <SevenComboFormula1Tab data={data} />
            )}

            {/* === FORMULA 2 TAB (Next-draw) === */}
            {mainTab === 'formula2' && (
              <SevenComboFormula2Tab data={data} />
            )}

            {mainTab === 'rankings' && (
            <>
            <div className="grid grid-cols-1 gap-6">

              {/* Draws history (LEFT pane removed — superseded by Formula 1 tab) */}
              <div className="bg-white border border-gray-200 rounded overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>All Draws with 7-Combo Patterns</h3>
                  <p className="text-xs text-gray-500 mt-1">B1-B5 + LS1 + LS2 differences + pattern + probability</p>
                </div>
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                  <table className="w-full text-xs" style={{ minWidth: '700px' }}>
                    <thead className="bg-gray-100 sticky top-0 z-[2]">
                      <tr>
                        <th className="px-1 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center w-[40px]">Mon</th>
                        <th className="px-1 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center w-[40px]">Yr</th>
                        <th className="px-1 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-blue-100 w-[35px]">B1</th>
                        <th className="px-1 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-blue-100 w-[35px]">B2</th>
                        <th className="px-1 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-blue-100 w-[35px]">B3</th>
                        <th className="px-1 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-blue-100 w-[35px]">B4</th>
                        <th className="px-1 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-blue-100 w-[35px]">B5</th>
                        <th className="px-1 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-amber-100 w-[35px]">LS1</th>
                        <th className="px-1 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-amber-100 w-[35px]">LS2</th>
                        <th className="px-1 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-purple-100" style={{ minWidth: '220px' }}>Pattern</th>
                        <th className="px-1 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-green-100 w-[50px]">Prob</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.grand_total && (
                        <tr className="bg-gray-800 text-white font-bold">
                          <td className="px-1 py-2 text-center text-xs" colSpan={2}>G.Total {data.current_year}</td>
                          <td className="px-1 py-2 font-mono text-center">{data.grand_total.ball_1}</td>
                          <td className="px-1 py-2 font-mono text-center">{data.grand_total.ball_2}</td>
                          <td className="px-1 py-2 font-mono text-center">{data.grand_total.ball_3}</td>
                          <td className="px-1 py-2 font-mono text-center">{data.grand_total.ball_4}</td>
                          <td className="px-1 py-2 font-mono text-center">{data.grand_total.ball_5}</td>
                          <td className="px-1 py-2 font-mono text-center">{data.grand_total.lucky_star_1}</td>
                          <td className="px-1 py-2 font-mono text-center">{data.grand_total.lucky_star_2}</td>
                          <td colSpan={2} className="px-1 py-2 text-center text-xs">{data.grand_total.count} draws</td>
                        </tr>
                      )}
                      {cyMonthOrder.map(m => {
                        const draws = cyGrouped[m] || [];
                        const mt = data.monthly_totals?.find(t => t.month === m);
                        return (
                          <React.Fragment key={`cy-${m}`}>
                            {draws.map((d, idx) => <DrawRow key={d.draw_number} d={d} idx={idx} />)}
                            {mt && <TotalRow label={`Total ${mt.month_name}`} data={mt} count={mt.count} />}
                          </React.Fragment>
                        );
                      })}
                      <tr className="bg-[#002FA7]">
                        <td colSpan={11} className="px-3 py-2 text-white font-bold text-xs text-center">Historical Data (Before {data.current_year})</td>
                      </tr>
                      {histYearOrder.map(yr => {
                        const draws = histGrouped[yr] || [];
                        const yt = data.yearly_totals?.find(t => t.year === yr);
                        return (
                          <React.Fragment key={`hist-${yr}`}>
                            <tr className="bg-amber-100">
                              <td colSpan={11} className="px-3 py-1.5 font-bold text-xs text-amber-900">{yr} ({draws.length} shown{yt ? `, ${yt.count} total` : ''})</td>
                            </tr>
                            {draws.map((d, idx) => <DrawRow key={d.draw_number} d={d} idx={idx} />)}
                            {yt && <TotalRow label={`Total ${yr}`} data={yt} count={yt.count} extraCls="bg-amber-200" />}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {histPage < totalHistPages && (
                  <div className="p-3 border-t border-gray-200 text-center">
                    <Button variant="ghost" size="sm" onClick={() => setHistPage(p => p + 1)} className="text-xs text-blue-600">
                      Load More ({histPage * HIST_PER_PAGE} / {histDraws.length} shown)
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Analysis Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-white border border-gray-200 rounded p-4">
                <h4 className="font-bold text-gray-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Last Draw Pattern</h4>
                {data.current_year_draws?.[0] && (
                  <div>
                    <PatternDisplay pattern={data.current_year_draws[0].pattern} />
                    <p className="text-xs text-gray-500 mt-2">Rank #{data.current_year_draws[0].pattern_rank} | {(data.current_year_draws[0].pattern_pct * 100).toFixed(1)}% overall</p>
                  </div>
                )}
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <h4 className="font-bold text-gray-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Most Common in {data.current_year}</h4>
                <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                  {(() => {
                    const freq = {};
                    (data.current_year_draws || []).forEach(d => { freq[d.pattern] = (freq[d.pattern] || 0) + 1; });
                    return Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([pat, count]) => (
                      <div key={pat} className="flex items-center justify-between bg-indigo-50 rounded px-2 py-1.5">
                        <PatternDisplay pattern={pat} /><span className="text-sm text-indigo-700 font-bold ml-2">{count}x</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <h4 className="font-bold text-gray-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Not Yet Seen in {data.current_year}</h4>
                <p className="text-xs text-gray-500 mb-2">High-probability combos missing in {data.current_year}</p>
                <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                  {(() => {
                    const seen = new Set((data.current_year_draws || []).map(d => d.pattern));
                    return (data.ranked_patterns || []).filter(p => !seen.has(p.series)).slice(0, 5).map(p => (
                      <div key={p.series} className="flex items-center justify-between bg-orange-50 rounded px-2 py-1.5">
                        <PatternDisplay pattern={p.series} /><span className="text-sm text-orange-700 font-bold ml-2">{(p.percentage * 100).toFixed(1)}%</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
            </>
            )}

            {/* Top 4 Combo Predictions Tab */}
            {mainTab === 'top4pred' && data.top4_predictions && (() => {
              const ballLabels = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5', lucky_star_1: 'LS1', lucky_star_2: 'LS2' };
              const allKeys = ['ball_1','ball_2','ball_3','ball_4','ball_5','lucky_star_1','lucky_star_2'];
              return (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-[#002FA7] to-indigo-800 rounded-xl p-5 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <Target size={24} weight="bold" />
                      <h2 className="text-xl font-bold">Top 4 Combo Number Predictions</h2>
                    </div>
                    <p className="text-sm text-blue-200">
                      Based on Prediction Score (Frequency x Overdue). Each combo tells the direction (UP/DOWN) per position.
                      The top difference matching that direction is used to predict the actual number.
                    </p>
                    <p className="text-xs text-blue-300 mt-1">
                      Last values: {allKeys.map(bk => `${ballLabels[bk]}=${data.last_values?.[bk]}`).join(' | ')}
                    </p>
                  </div>

                  {/* 2026 Per-Ball UP/DOWN/EQUAL */}
                  {data.ball_dir_2026 && (
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <div className="p-3 border-b bg-gray-50">
                        <h3 className="font-bold text-gray-900">2026 Direction Count Per Ball</h3>
                        <p className="text-xs text-gray-500">How many times each ball went UP, DOWN, or stayed EQUAL in {data.current_year} ({data.ball_dir_2026?.ball_1?.total || 0} draws)</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-left text-[10px] uppercase" rowSpan={2}>Ball</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase bg-blue-50 border-b border-blue-200" colSpan={3}>Till 2025 ({data.ball_dir_2025?.ball_1?.total || 0} draws)</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase bg-amber-50 border-b border-amber-200" colSpan={3}>2026 ({data.ball_dir_2026?.ball_1?.total || 0} draws)</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase" rowSpan={2}>Dominant 2026</th>
                            </tr>
                            <tr>
                              <th className="px-2 py-1.5 text-center text-[10px] uppercase text-emerald-700 bg-blue-50">UP</th>
                              <th className="px-2 py-1.5 text-center text-[10px] uppercase text-red-700 bg-blue-50">DN</th>
                              <th className="px-2 py-1.5 text-center text-[10px] uppercase text-gray-600 bg-blue-50">=</th>
                              <th className="px-2 py-1.5 text-center text-[10px] uppercase text-emerald-700 bg-amber-50">UP</th>
                              <th className="px-2 py-1.5 text-center text-[10px] uppercase text-red-700 bg-amber-50">DN</th>
                              <th className="px-2 py-1.5 text-center text-[10px] uppercase text-gray-600 bg-amber-50">=</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allKeys.map((bk) => {
                              const bd = data.ball_dir_2026[bk];
                              const bh = data.ball_dir_2025?.[bk];
                              if (!bd) return null;
                              const dominant = bd.up > bd.down ? 'UP' : bd.down > bd.up ? 'DOWN' : 'EQUAL';
                              return (
                                <tr key={bk} className="border-b">
                                  <td className="px-3 py-2 font-bold">{ballLabels[bk]}</td>
                                  <td className="px-2 py-2 text-center font-mono bg-blue-50/30">{bh?.up || 0} <span className="text-gray-400">({bh?.up_pct || 0}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono bg-blue-50/30">{bh?.down || 0} <span className="text-gray-400">({bh?.down_pct || 0}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono bg-blue-50/30">{bh?.equal || 0} <span className="text-gray-400">({bh?.equal_pct || 0}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-emerald-700 bg-amber-50/30">{bd.up} <span className="text-gray-400 font-normal">({bd.up_pct}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-red-600 bg-amber-50/30">{bd.down} <span className="text-gray-400 font-normal">({bd.down_pct}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-gray-600 bg-amber-50/30">{bd.equal} <span className="text-gray-400 font-normal">({bd.equal_pct}%)</span></td>
                                  <td className="px-3 py-2 text-center">
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                      dominant === 'UP' ? 'bg-emerald-100 text-emerald-700' : dominant === 'DOWN' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                                    }`}>{dominant}</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                            {(() => {
                              const t25up = allKeys.reduce((s, bk) => s + (data.ball_dir_2025?.[bk]?.up || 0), 0);
                              const t25dn = allKeys.reduce((s, bk) => s + (data.ball_dir_2025?.[bk]?.down || 0), 0);
                              const t25eq = allKeys.reduce((s, bk) => s + (data.ball_dir_2025?.[bk]?.equal || 0), 0);
                              const t25all = t25up + t25dn + t25eq || 1;
                              const t26up = allKeys.reduce((s, bk) => s + (data.ball_dir_2026[bk]?.up || 0), 0);
                              const t26dn = allKeys.reduce((s, bk) => s + (data.ball_dir_2026[bk]?.down || 0), 0);
                              const t26eq = allKeys.reduce((s, bk) => s + (data.ball_dir_2026[bk]?.equal || 0), 0);
                              const t26all = t26up + t26dn + t26eq || 1;
                              return (
                                <tr>
                                  <td className="px-3 py-2 font-bold text-gray-700">Total</td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-emerald-700 bg-blue-50/30">{t25up} <span className="text-gray-400 font-normal">({(t25up/t25all*100).toFixed(1)}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-red-600 bg-blue-50/30">{t25dn} <span className="text-gray-400 font-normal">({(t25dn/t25all*100).toFixed(1)}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-gray-600 bg-blue-50/30">{t25eq} <span className="text-gray-400 font-normal">({(t25eq/t25all*100).toFixed(1)}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-emerald-700 bg-amber-50/30">{t26up} <span className="text-gray-400 font-normal">({(t26up/t26all*100).toFixed(1)}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-red-600 bg-amber-50/30">{t26dn} <span className="text-gray-400 font-normal">({(t26dn/t26all*100).toFixed(1)}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-gray-600 bg-amber-50/30">{t26eq} <span className="text-gray-400 font-normal">({(t26eq/t26all*100).toFixed(1)}%)</span></td>
                                  <td className="px-3 py-2 text-center"></td>
                                </tr>
                              );
                            })()}
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Summary: Top 4 predicted lines */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.top4_predictions.map((combo, ci) => (
                      <div key={combo.pattern} className={`bg-white border rounded-lg overflow-hidden ${ci === 0 ? 'ring-2 ring-[#002FA7]' : ''}`}>
                        <div className={`px-4 py-3 flex items-center justify-between ${ci === 0 ? 'bg-gradient-to-r from-[#002FA7] to-indigo-700 text-white' : 'bg-gradient-to-r from-slate-700 to-slate-800 text-white'}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold">#{ci + 1}</span>
                            <PatternDisplay pattern={combo.pattern} />
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold">{combo.pred_score}</div>
                            <div className="text-[10px] text-white/60">Score</div>
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="flex items-center justify-center gap-2 mb-3">
                            {allKeys.map((bk) => {
                              const bp = combo.balls[bk];
                              const isBall = bk.startsWith('ball');
                              return (
                                <div key={bk} className="text-center">
                                  <div className="text-[9px] text-gray-400 mb-0.5">{ballLabels[bk]}</div>
                                  <div className={`inline-flex w-10 h-10 rounded-full items-center justify-center font-mono font-bold text-sm ${
                                    ci === 0
                                      ? (isBall ? 'bg-[#002FA7] text-white' : 'bg-amber-500 text-white')
                                      : (isBall ? 'bg-slate-700 text-white' : 'bg-amber-400 text-amber-900')
                                  }`}>{bp.top_pick}</div>
                                  <div className={`text-[9px] font-mono font-bold mt-0.5 ${bp.direction === 'UP' ? 'text-emerald-600' : bp.direction === 'DOWN' ? 'text-red-500' : 'text-gray-400'}`}>
                                    {bp.direction === 'UP' ? '\u25B2' : bp.direction === 'DOWN' ? '\u25BC' : '='} {bp.picks?.[0]?.diff_label || '0'}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex items-center justify-center gap-4 text-[10px] text-gray-500">
                            <span>Rank #{combo.rank}</span>
                            <span>{combo.times}x ({(combo.percentage * 100).toFixed(2)}%)</span>
                            <span>Gap: {combo.gap}</span>
                            <span className={combo.overdue_ratio > 1 ? 'text-red-600 font-bold' : ''}>{combo.overdue_ratio}x overdue</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Detailed per-ball breakdown */}
                  {data.top4_predictions.map((combo, ci) => (
                    <div key={combo.pattern} className="bg-white border rounded-lg overflow-hidden">
                      <div className="bg-gray-100 px-4 py-2 flex items-center gap-2">
                        <span className="font-bold text-sm text-gray-700">#{ci + 1} Detail:</span>
                        <PatternDisplay pattern={combo.pattern} />
                        <span className="text-xs text-gray-500 ml-auto">Score: {combo.pred_score} | Overdue: {combo.overdue_ratio}x</span>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3">
                          {allKeys.map((bk) => {
                            const bp = combo.balls[bk];
                            return (
                              <div key={bk} className="border rounded-lg overflow-hidden">
                                <div className={`px-2 py-1.5 text-white text-center text-xs font-bold ${bp.direction === 'UP' ? 'bg-emerald-600' : bp.direction === 'DOWN' ? 'bg-red-500' : 'bg-gray-500'}`}>
                                  {ballLabels[bk]} {bp.direction === 'UP' ? '\u25B2' : bp.direction === 'DOWN' ? '\u25BC' : '='} (last: {bp.last_value})
                                </div>
                                <div className="divide-y">
                                  {(bp.picks || []).slice(0, 4).map((pick, pi) => (
                                    <div key={pick.diff} className={`px-2 py-1.5 flex items-center justify-between ${pi === 0 ? 'bg-blue-50' : ''}`}>
                                      <div className="flex items-center gap-1">
                                        <span className={`font-mono font-bold text-sm ${pick.diff > 0 ? 'text-emerald-600' : pick.diff < 0 ? 'text-red-500' : 'text-gray-400'}`}>{pick.diff_label}</span>
                                        <span className="inline-flex w-6 h-6 rounded-full bg-gray-700 text-white items-center justify-center font-mono font-bold text-[9px]">{pick.target}</span>
                                      </div>
                                      <span className="text-[10px] text-gray-500 font-mono">{pick.count}x</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </>
        ) : null}
      </main>
    </div>
  );
}


// =====================================================================================
// Helper: render 7-combo pattern as compact arrow strip
// =====================================================================================
function SevenComboBadge({ pattern }) {
  if (!pattern) return null;
  const parts = pattern.split(',');
  return (
    <span className="font-mono font-bold inline-flex gap-0.5 items-center" title={pattern}>
      {parts.map((p, i) => {
        const idx = i < 5 ? `B${i + 1}` : `LS${i - 4}`;
        const Icon = p === 'UP' ? ArrowUp : p === 'DOWN' ? ArrowDown : Minus;
        const color = p === 'UP' ? 'text-emerald-600' : p === 'DOWN' ? 'text-red-500' : 'text-gray-400';
        return (
          <span key={i} className={`flex flex-col items-center ${color}`}>
            <Icon size={14} weight="bold" />
            <span className="text-[8px] font-mono opacity-50">{idx}</span>
          </span>
        );
      })}
    </span>
  );
}

// =====================================================================================
// Formula 1 Tab — Long-term ranked patterns by F1 v_adj
// =====================================================================================
function SevenComboFormula1Tab({ data }) {
  const rows = [...(data?.ranked_patterns || [])].sort((a, b) => (b.pred_score_volatility_adj || 0) - (a.pred_score_volatility_adj || 0));
  if (rows.length === 0) return <div className="bg-white border rounded p-6 text-sm text-gray-500">No data.</div>;
  const top3Threshold = rows.length >= 3 ? rows[2].pred_score_volatility_adj : 0;
  return (
    <div className="space-y-4" data-testid="f1-seven-combo-tab">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-5 text-white shadow-lg">
        <div className="flex items-center gap-2 mb-1">
          <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] font-black tracking-wider">FORMULA 1</span>
          <h2 className="text-xl font-black">Long-Term Predictor (multiplicative)</h2>
        </div>
        <p className="text-[11px] text-blue-100 font-mono">Score = Freq% × (overdue_ratio)^0.5 × volatility × recency × consec-miss · {rows.length} unique patterns ranked</p>
      </div>
      <div className="bg-white border-2 border-blue-200 rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto max-h-[600px]">
          <table className="w-full text-xs">
            <thead className="bg-blue-50 sticky top-0 border-b-2 border-blue-200">
              <tr className="text-[10px] uppercase tracking-wider text-blue-900">
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">Pattern</th>
                <th className="px-2 py-2 text-center">Count</th>
                <th className="px-2 py-2 text-center">Freq %</th>
                <th className="px-2 py-2 text-center">Exp Gap</th>
                <th className="px-2 py-2 text-center">Cur Gap</th>
                <th className="px-2 py-2 text-center">Overdue</th>
                <th className="px-2 py-2 text-center">CV</th>
                <th className="px-2 py-2 text-center">Recency</th>
                <th className="px-2 py-2 text-center">Consec</th>
                <th className="px-2 py-2 text-center bg-blue-100 font-black">Score (v_adj)</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 100).map((r, i) => {
                const isTop3 = (r.pred_score_volatility_adj || 0) >= top3Threshold && top3Threshold > 0;
                return (
                  <tr key={r.series} className={`border-b border-gray-100 ${isTop3 ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-testid={`f1-row-${i}`}>
                    <td className="px-2 py-2 font-mono text-gray-500">#{i + 1}</td>
                    <td className="px-2 py-2"><SevenComboBadge pattern={r.series} /></td>
                    <td className="px-2 py-2 text-center font-mono font-bold">{r.times}</td>
                    <td className="px-2 py-2 text-center font-mono">{(r.percentage * 100).toFixed(2)}%</td>
                    <td className="px-2 py-2 text-center font-mono text-gray-500">{r.expected_gap}</td>
                    <td className="px-2 py-2 text-center font-mono">{r.gap}</td>
                    <td className={`px-2 py-2 text-center font-mono font-bold ${r.overdue_ratio >= 2 ? 'text-red-600' : r.overdue_ratio >= 1 ? 'text-amber-600' : 'text-gray-500'}`}>{r.overdue_ratio}x</td>
                    <td className="px-2 py-2 text-center font-mono text-[10px] text-gray-500">{r.gap_cv}</td>
                    <td className={`px-2 py-2 text-center font-mono text-[10px] ${r.recency_factor > 1 ? 'text-emerald-700' : r.recency_factor < 1 ? 'text-red-600' : 'text-gray-500'}`}>{r.recency_factor}</td>
                    <td className={`px-2 py-2 text-center font-mono text-[10px] ${r.consec_miss_factor > 1 ? 'text-emerald-700' : 'text-gray-500'}`}>{r.consec_miss_factor}</td>
                    <td className="px-2 py-2 text-center" data-testid={`f1-score-${i}`}>
                      {isTop3 ? (
                        <span className="inline-flex w-12 h-12 rounded-full items-center justify-center bg-blue-500 text-white font-mono font-black text-sm shadow-md ring-2 ring-blue-200">
                          {r.pred_score_volatility_adj}
                        </span>
                      ) : (
                        <span className="font-mono font-bold text-blue-800">{r.pred_score_volatility_adj}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length > 100 && (
          <div className="p-2 text-center text-[10px] text-gray-500 bg-gray-50 border-t">Showing top 100 of {rows.length} patterns</div>
        )}
      </div>
    </div>
  );
}

// =====================================================================================
// Formula 2 Tab — Next-draw additive scoring
// =====================================================================================
function SevenComboFormula2Tab({ data }) {
  const rows = [...(data?.ranked_patterns || [])].sort((a, b) => (b.combined_score_v2 || 0) - (a.combined_score_v2 || 0));
  if (rows.length === 0) return <div className="bg-white border rounded p-6 text-sm text-gray-500">No data.</div>;
  const top3Threshold = rows.length >= 3 ? rows[2].combined_score_v2 : 0;
  const lastPat = data.convergence?.last_pattern;
  const dowMap = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const nextDow = data.convergence?.next_dow != null ? dowMap[data.convergence.next_dow] : '?';
  return (
    <div className="space-y-4" data-testid="f2-seven-combo-tab">
      <div className="bg-gradient-to-r from-orange-600 to-amber-600 rounded-xl p-5 text-white shadow-lg">
        <div className="flex items-center gap-2 mb-1">
          <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] font-black tracking-wider">FORMULA 2</span>
          <h2 className="text-xl font-black">Next-Draw Predictor (additive 6-component)</h2>
        </div>
        <p className="text-[11px] text-orange-100 font-mono">
          Last: <SevenComboBadge pattern={lastPat} />  ·  Next DoW: <span className="font-bold text-white">{nextDow}</span>  ·  Score = T + G + Y + B + DoW + M2
        </p>
      </div>
      <div className="bg-white border-2 border-orange-200 rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto max-h-[600px]">
          <table className="w-full text-xs">
            <thead className="bg-orange-50 sticky top-0 border-b-2 border-orange-200">
              <tr className="text-[10px] uppercase tracking-wider text-orange-900">
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">Pattern</th>
                <th className="px-2 py-2 text-center">T</th>
                <th className="px-2 py-2 text-center">G</th>
                <th className="px-2 py-2 text-center">Y</th>
                <th className="px-2 py-2 text-center">B</th>
                <th className="px-2 py-2 text-center">DoW</th>
                <th className="px-2 py-2 text-center">M2</th>
                <th className="px-2 py-2 text-center bg-orange-100 font-black">Score</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 100).map((r, i) => {
                const isTop3 = (r.combined_score_v2 || 0) >= top3Threshold && top3Threshold > 0;
                return (
                  <tr key={r.series} className={`border-b border-gray-100 ${isTop3 ? 'bg-orange-50' : 'hover:bg-gray-50'}`} data-testid={`f2-row-${i}`}>
                    <td className="px-2 py-2 font-mono text-gray-500">#{i + 1}</td>
                    <td className="px-2 py-2"><SevenComboBadge pattern={r.series} /></td>
                    <td className="px-2 py-2 text-center font-mono">
                      <div className="font-bold text-orange-700">{r.trans_score}</div>
                      <div className="text-[9px] text-gray-400">({r.trans_pct}%)</div>
                    </td>
                    <td className="px-2 py-2 text-center font-mono">
                      <div className="font-bold text-orange-700">{r.gap_score_v2}</div>
                      <div className="text-[9px] text-gray-400">({r.overdue_ratio}x)</div>
                    </td>
                    <td className="px-2 py-2 text-center font-mono">
                      <div className={`font-bold ${(r.year_score || 0) > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>{r.year_score}</div>
                      <div className="text-[9px] text-gray-400">({r.year_diff > 0 ? '+' : ''}{r.year_diff}%)</div>
                    </td>
                    <td className="px-2 py-2 text-center font-mono">
                      <div className="font-bold text-orange-700">{r.base_score}</div>
                      <div className="text-[9px] text-gray-400">({(r.percentage * 100).toFixed(1)}%)</div>
                    </td>
                    <td className="px-2 py-2 text-center font-mono">
                      <div className={`font-bold ${(r.dow_bonus || 0) > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>{r.dow_bonus}</div>
                    </td>
                    <td className="px-2 py-2 text-center font-mono">
                      <div className={`font-bold ${(r.markov2_score || 0) > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>{r.markov2_score}</div>
                      <div className="text-[9px] text-gray-400">({r.markov2_pct}%)</div>
                    </td>
                    <td className="px-2 py-2 text-center" data-testid={`f2-score-${i}`}>
                      {isTop3 ? (
                        <span className="inline-flex w-12 h-12 rounded-full items-center justify-center bg-orange-500 text-white font-mono font-black text-sm shadow-md ring-2 ring-orange-200">
                          {r.combined_score_v2}
                        </span>
                      ) : (
                        <span className="font-mono font-bold text-orange-800">{r.combined_score_v2}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length > 100 && (
          <div className="p-2 text-center text-[10px] text-gray-500 bg-gray-50 border-t">Showing top 100 of {rows.length} patterns</div>
        )}
      </div>
    </div>
  );
}
